import React, { useState, useCallback } from "react";
import styled from "styled-components";

const BREAKPOINT = 768;

const Wrapper = styled.div`
  position: relative;

  @media (min-width: ${BREAKPOINT}px) {
    position: static;
    display: contents;
  }
`;

const DesktopSlot = styled.div`
  display: none;

  @media (min-width: ${BREAKPOINT}px) {
    display: contents;
  }
`;

const MobileToggle = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINT}px) {
    display: flex;
    position: absolute;
    top: ${({ $offsetTop }) => `${$offsetTop}px`};
    // top: -10px; 
    left: ${({ $offsetLeft }) => `${$offsetLeft}px`};
    // left: -10px;
    z-index: 9999;
  }
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.8rem;
  line-height: 1;
  padding: 4px;
  color: ${({ colors }) => colors?.buttonBackground || "#2C4960"};

  @media (max-width: ${BREAKPOINT}px) {
    position: relative;
  }

  @media (min-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  display: ${({ open }) => (open ? "block" : "none")};

  @media (min-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 80%;
  max-width: 320px;
  background: #fff;
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(-100%)")};
  transition: transform 0.25s ease-in-out;
  z-index: 999;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;

  @media (min-width: ${BREAKPOINT}px) {
    display: none;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 600;
  font-size: 1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
`;

const DrawerBody = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
`;

export default function ResponsiveActions({
  colors,
  children,
  renderActions,
  toggleLabel = "Actions",
  mobileToggleTop = -15,
  mobileToggleLeft = -15,
}) {
  const [open, setOpen] = useState(false);

  const produceContent = useCallback(() => {
    let node;
    if (typeof renderActions === "function") {
      node = renderActions();
    } else if (typeof children === "function") {
      node = children();
    } else {
      node = children;
    }

    if (React.isValidElement(node)) {
      return React.cloneElement(node);
    }

    if (Array.isArray(node)) {
      return node.map((child, index) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { key: child.key ?? index })
          : child
      );
    }

    return node ?? null;
  }, [renderActions, children]);

  const preview = produceContent();
  if (!preview) return null;

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  return (
    <Wrapper>
      <DesktopSlot>{preview}</DesktopSlot>

      <MobileToggle $offsetTop={mobileToggleTop} $offsetLeft={mobileToggleLeft}>
        <ToggleButton
          type="button"
          colors={colors}
          onClick={handleToggle}
          aria-label={`${toggleLabel} menu`}
        >
          ☰
        </ToggleButton>
      </MobileToggle>

      <Overlay open={open} onClick={handleClose} />

      <Drawer open={open}>
        <DrawerHeader>
          <span>{toggleLabel}</span>
          <CloseButton type="button" onClick={handleClose}>
            ×
          </CloseButton>
        </DrawerHeader>
        <DrawerBody>{produceContent()}</DrawerBody>
      </Drawer>
    </Wrapper>
  );
}
